import React from 'react'
import {
  FormGroup,
  Divider,
  Button,
  RadioGroup,
  Radio,
} from '@blueprintjs/core'
import { Tooltip2, Popover2 } from '@blueprintjs/popover2'
import styled from 'styled-components'

import quarks from './quarks'

const StyledRadioGroup = styled(RadioGroup)`
  margin: ${quarks.margins.small};
`

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 0;
`

type RadioDropdownProps<T extends string> = {
  options: T[]
  getOptionText: (option: T | undefined, selected: boolean) => string
  selectedOption?: T
  setSelectedOption: (newlySelectedOption?: T) => void
  getDisabled?: (
    option: T | undefined,
    selectedOption: T | undefined,
  ) => boolean
  tooltipContent?: string | JSX.Element | undefined
}

const RadioDropdown = <T extends string>({
  options,
  getOptionText,
  selectedOption,
  setSelectedOption,
  getDisabled,
  tooltipContent,
}: RadioDropdownProps<T>) => {
  // helps make TS happy
  const isOption = (value: unknown): value is typeof options[number] =>
    (options as unknown[]).includes(value)

  return (
    <Popover2
      position="bottom-left"
      content={
        <StyledFormGroup>
          <StyledRadioGroup
            onChange={(e) => {
              const value = e.currentTarget.value
              if (isOption(value)) {
                setSelectedOption(selectedOption === value ? undefined : value)
              } else if (!value?.length) {
                setSelectedOption(undefined)
              }
            }}
            selectedValue={selectedOption ?? ''}
          >
            <Radio
              disabled={getDisabled?.(undefined, selectedOption)}
              label={getOptionText(undefined, selectedOption === undefined)}
              value=""
            />
            <Divider />
            {options.map((value) => (
              <Radio
                key={value}
                disabled={getDisabled?.(value, selectedOption)}
                label={getOptionText(value, selectedOption === value)}
                value={value}
              />
            ))}
          </StyledRadioGroup>
        </StyledFormGroup>
      }
    >
      <Tooltip2 position="top" content={tooltipContent}>
        <Button>{getOptionText(selectedOption, true)}</Button>
      </Tooltip2>
    </Popover2>
  )
}

export default RadioDropdown
